import React, { useMemo, useEffect, useState } from 'react';
import Link from 'next/link';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';

import { RootState } from '../redux';
import { getSearchedCopyrights } from '../redux/copyrights/selectors';
import { sendAnalytics } from '../redux/analytics/actions';
import { searchCopyrights } from '../redux/copyrights/search-copyrights/actions';
import { fetchCategories } from '../redux/categories/actions';
import { getCategories } from '../redux/categories/selectors';

import { MainLayout } from '../components';
import { Copyright } from '../redux/copyrights/interfaces';

import { CheckBoxInput } from '../components/forms/form-elements';

const SearchPage: NextPage = () => {
  const dispatch = useDispatch();
  const router = useRouter();

  const [copyrights, setCopyrights] = useState<Copyright[]>([]);
  const [filters, setFilters] = useState<string[]>([]);

  const categoriesSelector = useMemo(getCategories, []);
  const categories = useSelector((state: RootState) => categoriesSelector(state));

  const copyrightsSelector = useMemo(getSearchedCopyrights, []);
  const _copyrights = useSelector((state: RootState) => copyrightsSelector(state));

  useEffect(() => {
    dispatch(
      sendAnalytics({
        event: 'searchPage',
        category: 'Navigation',
        meta: {},
      }),
    );

    const { q: term } = router.query;

    if (term && copyrights.length < 1) {
      dispatch(searchCopyrights(term as string));
    }
  }, []);

  useEffect(() => {
    if (categories.length < 1) dispatch(fetchCategories());
  }, []);

  const updateFilters = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (e.target.checked) {
      setFilters([...filters, value]);
    } else {
      setFilters((_filters) => _filters.filter((filter) => filter !== value));
    }
  };

  useEffect(() => {
    if (!filters.length) {
      setCopyrights(_copyrights);
      return;
    }

    const results = _copyrights.filter(({ category }) => filters.includes(category.name));
    setCopyrights(results);
  }, [filters, _copyrights]);

  return (
    <MainLayout>
      <div className="mt-4">
        <div className="px-4 max-width lg:px-20">
          {copyrights.length < 1 && !filters.length ? (
            <p>No copyrights match your creteria</p>
          ) : (
            <div>
              <p className="text-lg">Filter By Category</p>
              <div className="flex justify-between my-4">
                {categories.map(({ id, name }) => (
                  <div key={id} className="w-1/3">
                    <CheckBoxInput label={name} onChange={updateFilters} />
                  </div>
                ))}
              </div>

              <div className="pt-8">
                <p className="text-xl">Search Results</p>

                {copyrights.map((copyright) => (
                  <div key={copyright.id} className="mb-4">
                    <Link href={`/copyrights/${copyright.id}`}>
                      <a className="text-2xl text-blue-500">
                        {copyright.title}, {copyright.category.name || ''}
                      </a>
                    </Link>
                    <div>
                      <p className="text-sm italic">
                        <span>Owners: </span>
                        {copyright.owners
                          .map((item) => `${item.firstName || ''} ${item.lastName || ''}`)
                          .join(', ')}
                      </p>

                      <p className="text-sm italic">
                        <span>Authors: </span>
                        {copyright.authors.map((item) => item.name || '').join(', ')}
                      </p>

                      {copyright.producers.length > 0 && (
                        <p className="text-sm italic">
                          <span>Producers: </span>
                          {copyright.producers.map((item) => item.name || '').join(', ')}
                        </p>
                      )}

                      {copyright.performers.length > 0 && (
                        <p className="text-sm italic">
                          <span>Performers: </span>
                          {copyright.performers.map((item) => item.name || '').join(', ')}
                        </p>
                      )}

                      {copyright.publishers.length > 0 && (
                        <p className="text-sm italic">
                          <span>Publishers: </span>
                          {copyright.publishers.map((item) => item.name || '').join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

SearchPage.getInitialProps = async ({ store, query }) => {
  const { q: term } = query;

  if (term) {
    store.dispatch(searchCopyrights(term as string));
  }

  store.dispatch(fetchCategories());

  return {};
};

export default SearchPage;
